/**
 * Utility functions for handling dates with timezone awareness
 */

/**
 * Format a date string to the user's timezone with relative time display
 * @param {string} dateString - ISO date string from backend
 * @param {string} userTimezone - User's timezone (e.g., 'America/New_York')
 * @returns {string} Formatted date string
 */
export const formatLastEdited = (dateString, userTimezone = 'America/New_York') => {
  if (!dateString) return "Never";
  
  try {
    // Parse the date string and convert to user's timezone
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate time difference in minutes
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    // For recent times, show relative time
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    
    // For older dates, show formatted date in user's timezone
    return formatDateInTimezone(date, userTimezone);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Format a date to the user's timezone
 * @param {Date} date - Date object
 * @param {string} userTimezone - User's timezone
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, userTimezone = 'America/New_York', options = {}) => {
  if (!date) return "Never";
  
  try {
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimezone
    };
    
    const formatOptions = { ...defaultOptions, ...options };
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (error) {
    console.error("Error formatting date in timezone:", error);
    return date.toLocaleDateString();
  }
};

/**
 * Format a date with full date and time in user's timezone
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} userTimezone - User's timezone
 * @returns {string} Formatted date string
 */
export const formatFullDateInTimezone = (dateInput, userTimezone = 'America/New_York') => {
  if (!dateInput) return "Never";
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    return formatDateInTimezone(date, userTimezone, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error("Error formatting full date:", error);
    return "Invalid date";
  }
};

/**
 * Get display date with fallback logic (last_edited_at or created_at)
 * @param {object} item - Object with last_edited_at and created_at properties
 * @param {string} userTimezone - User's timezone
 * @returns {string} Formatted date string
 */
export const getDisplayDate = (item, userTimezone = 'America/New_York') => {
  // Use last_edited_at if valid, otherwise fall back to created_at
  const dateToUse = (item.last_edited_at && item.last_edited_at !== null) 
    ? item.last_edited_at 
    : item.created_at;
    
  return formatDateInTimezone(new Date(dateToUse), userTimezone);
};
